///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { DeviceSectorsFeature } from './device-sectors-feature';
import { GtthHistoryService } from '../../engine/history-service';
import { TopologyLinkTypeEnum } from '../../../../services/project-socket/dto/topology-types.model';
import { SectorFeature } from './sector-feature';
import { LinkFeature } from '../link/link-feature';
import { isDefined } from '@siklu/sui-lib/utils';
import { LinkProduct } from '../link/link-product';

export class SectorsService {
    private readonly sectorsMap: Map<number, DeviceSectorsFeature>;

    private readonly buMustSectors: Map<number, DeviceSectorsFeature>;

    constructor() {
        this.sectorsMap = new Map();
        this.buMustSectors = new Map();
    }

    redrawSectors(historyService: GtthHistoryService) {
        this.hideAndClear();

        const linksIterator = historyService.logicalTopology.getLinksIterator();

        const activeLinksWithSectors = this.getActiveLinksWithSectors(linksIterator);

        this.createSectorsForBU_withActiveLinks(historyService, activeLinksWithSectors);

        for (const station of historyService.logicalTopology.getStationsIterator()) {
            station.checkHasSectors();
            station.refreshState();
        }

        this.showSectors(historyService);
    }

    getDeviceSectors(p2mpRepSectorIndex: number): DeviceSectorsFeature {
        return this.sectorsMap.get(p2mpRepSectorIndex);
    }

    getSector(sectorId: number, sectorInDevice: number): SectorFeature {
        return this.sectorsMap.has(sectorId) ? this.sectorsMap.get(sectorId).getDeviceSector(sectorInDevice) : undefined;
    }

    refreshColor(): void {
        this.sectorsMap.forEach((deviceSectors: DeviceSectorsFeature) => deviceSectors.refreshColor());
        this.buMustSectors.forEach((deviceSectors: DeviceSectorsFeature) => deviceSectors.refreshColor());
    }

    hideAndClear(): void {
        if (this.sectorsMap) {
            this.sectorsMap.forEach((deviceSectors: DeviceSectorsFeature) => deviceSectors.hide());
            this.sectorsMap.clear();
        }
        if (this.buMustSectors) {
            this.buMustSectors.forEach((deviceSectors: DeviceSectorsFeature) => deviceSectors.hide());
            this.buMustSectors.clear();
        }
    }

    getStationSectors(stationId: number): DeviceSectorsFeature[] {
        const sectors = [];
        this.sectorsMap.forEach((deviceSectors: DeviceSectorsFeature) => {
            if (deviceSectors.getSectorStationId() === stationId) {
                sectors.push(deviceSectors);
            }
        });
        this.buMustSectors.forEach((deviceSectors: DeviceSectorsFeature) => {
            if (deviceSectors.getSectorStationId() === stationId) {
                sectors.push(deviceSectors);
            }
        });
        return sectors;
    }

    getStationSectorsCount(stationId: number): number {
        let sectorsCount = 0;
        this.sectorsMap.forEach((deviceSectors: DeviceSectorsFeature) => {
            if (deviceSectors.getSectorStationId() === stationId) {
                sectorsCount += deviceSectors.getSectorsCount();
            }
        });
        this.buMustSectors.forEach((deviceSectors: DeviceSectorsFeature) => {
            if (deviceSectors.getSectorStationId() === stationId) {
                sectorsCount += deviceSectors.getSectorsCount();
            }
        });
        return sectorsCount;
    }

    private createSectorsForBU_withActiveLinks(historyService: GtthHistoryService, activeLinksWithSectors: LinkFeature[]): void {
        for (const link of activeLinksWithSectors) {
            const sectorStationId = link.getSectorStationId();
            const products = historyService.bomReportService.getLinkProducts(link);

            let linkProduct;
            let linkOppositeProduct;
            if (products.length === 2) {
                linkProduct = products[0].node.getId() === sectorStationId ? products[0] : products[1];
                linkOppositeProduct = products[0].node.getId() === sectorStationId ? products[1] : products[0];
            }

            const sectorP2mpIndex = link.getP2mpRepSectorIndex();
            if (!this.sectorsMap.has(sectorP2mpIndex)) {
                const deviceSectors = this.createDeviceSectors(historyService, link, linkProduct);
                this.sectorsMap.set(sectorP2mpIndex, deviceSectors);
            }

            this.sectorsMap.get(sectorP2mpIndex).addLink(link);

            if (linkOppositeProduct?.device?.isSectorBu && !isDefined(linkOppositeProduct.device.sectorIndex)) {
                this.createBuMustSector(historyService, linkOppositeProduct);
            }
        }
    }

    private createDeviceSectors(historyService: GtthHistoryService, link: LinkFeature, linkProduct: LinkProduct): DeviceSectorsFeature {
        const station = historyService.logicalTopology.getStation(link.getSectorStationId());

        const baseProduct = historyService.projectProductsService.getBaseRadioById(link.getBuUsedProduct());

        let obstructionAngle = station.getBlockedByPoleAngle();
        if (obstructionAngle < 0) {
            obstructionAngle = historyService.logicalTopology.autodesignP2mpPerLayer[station.getLayerIndex()].pole_block;
        }

        const deviceSectors = new DeviceSectorsFeature(
            link.getP2mpRepSectorIndex(),
            link.getLinkLayerIndex(),
            station.getId(),
            station.getPosition(),
            obstructionAngle,
            historyService,
            baseProduct.pdbConfig.sectorConfig
        );

        if (linkProduct) {
            for (const bomSectorConfig of linkProduct.device.deviceSectorsConfig) {
                if (bomSectorConfig.enabled) {
                    const sectorInDevice = deviceSectors.getDeviceSector(bomSectorConfig.sectorInDevice);
                    sectorInDevice.setActiveByBom(bomSectorConfig.enabled);
                    sectorInDevice.setAngle(bomSectorConfig.sectorInDeviceDirection);
                    sectorInDevice.setFrequency(bomSectorConfig.frequency);
                }
            }
        }

        return deviceSectors;
    }

    private createBuMustSector(historyService: GtthHistoryService, oppositeDevice: LinkProduct): void {
        const baseProduct = historyService.projectProductsService.getBaseRadioById(oppositeDevice.device.type);

        let obstructionAngle = oppositeDevice.node.getBlockedByPoleAngle();
        if (obstructionAngle < 0) {
            obstructionAngle = historyService.logicalTopology.autodesignP2mpPerLayer[oppositeDevice.node.getLayerIndex()].pole_block;
        }

        const deviceSectors = new DeviceSectorsFeature(
            undefined,
            oppositeDevice.node.getLayerIndex(),
            oppositeDevice.node.getId(),
            oppositeDevice.node.getPosition(),
            obstructionAngle,
            historyService,
            baseProduct.pdbConfig.sectorConfig
        );

        for (const bomSectorConfig of oppositeDevice.device.deviceSectorsConfig) {
            const sectorInDevice = deviceSectors.getDeviceSector(bomSectorConfig.sectorInDevice);
            sectorInDevice.setActiveByBom(bomSectorConfig.enabled);

            // check azimuth
            sectorInDevice.setAngle(bomSectorConfig.sectorInDeviceDirection);
            sectorInDevice.setFrequency(bomSectorConfig.frequency);
            sectorInDevice.recalculateSectorDistance(baseProduct.id);
        }

        this.buMustSectors.set(oppositeDevice.node.getId(), deviceSectors);
    }

    private showSectors(historyService: GtthHistoryService): void {
        if (historyService.projectUserSessionService.isShowSectors || historyService.projectUserSessionService.isShowAllSectors) {
            this.sectorsMap.forEach((deviceSectors: DeviceSectorsFeature) =>
                deviceSectors.draw(historyService.projectUserSessionService.isShowAllSectors)
            );
            this.buMustSectors.forEach((deviceSectors: DeviceSectorsFeature) =>
                deviceSectors.draw(historyService.projectUserSessionService.isShowAllSectors)
            );
        } else {
            historyService.logicalTopology.getNodeNamesSectorsToShow().forEach((stationName: string) => {
                const station = historyService.logicalTopology.getStationByName(stationName);
                this.getStationSectors(station.getId()).forEach((deviceSectors: DeviceSectorsFeature) => {
                    deviceSectors.draw(false);
                });
            });
        }
    }

    private getActiveLinksWithSectors(linksIterator: IterableIterator<LinkFeature>): LinkFeature[] {
        const activeLinks = [];

        for (const link of linksIterator) {
            if (
                !link.isActive() ||
                link.getLinkIsDisabled() ||
                link.getLinkType() === TopologyLinkTypeEnum.FIBER ||
                link.getSectorIndex() === -1 ||
                link.getSectorIndex() === -2 ||
                !link.isBuProductHasSectors()
            ) {
                continue;
            }
            activeLinks.push(link);
        }
        return activeLinks;
    }
}
